import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { hasModule } from '../../access/modules.js'
import { useCurrentUser } from '../../../hooks/useCurrentUser.js'
import { PROSPECTION_SOURCES } from '../sources.js'
import Prospects from '../../../pages/Prospects.jsx'
import Immobilier from '../../../pages/Immobilier.jsx'
import FranceTravailSearchPanel from '../../france-travail-candidates/components/FranceTravailSearchPanel.jsx'
import LinkedinSearchPanel from '../../linkedin/components/LinkedinSearchPanel.jsx'

const SOURCE_SCREENS = {
  b2b: () => <Prospects />,
  linkedin: () => <LinkedinSearchPanel />,
  // Les annonces acquises sont dans le CRM (base « Biens immobiliers »).
  immobilier: () => <Immobilier showAcquired={false} />,
  'france-travail': () => <FranceTravailSearchPanel />,
}

/** Carte d'une source (maquette Jean Louis V3) : badge, nom, état, sous-titre, description, action. */
function SourceCard({ source, enabled }) {
  return (
    <div
      className={`flex min-w-0 flex-col rounded-[12px] border bg-surface p-6 ${enabled ? 'border-border hover:border-brand/50' : 'border-border opacity-70'}`}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-border bg-surface-hover text-xs font-bold text-brand-text">
        {source.code}
      </span>
      <div className="mt-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-ink">{source.name}</h2>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${enabled ? 'bg-surface-active text-foreground-secondary' : 'bg-surface-active text-foreground-muted'}`}>
          {enabled ? 'Disponible' : 'Option non activée'}
        </span>
      </div>
      <p className="mt-2 text-[15px] font-semibold text-ink">{source.title}</p>
      <p className="mt-1.5 text-[13px] text-foreground-secondary">{source.description}</p>
      <div className="mt-5 flex items-center justify-between gap-3">
        {enabled ? (
          <Link
            to={`/dashboard/prospection/${source.key}`}
            className="rounded-full bg-brand px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-brand-strong"
          >
            Ouvrir la recherche
          </Link>
        ) : (
          <span className="rounded-full border border-border px-4 py-2 text-[13px] font-medium text-foreground-secondary" title="Demandez à un administrateur d'activer cette source">
            À activer par un administrateur
          </span>
        )}
        <span className="text-xs text-foreground-muted">{source.note}</span>
      </div>
    </div>
  )
}

function SourceChooser({ sources, enabledKeys }) {
  return (
    <div className="grid gap-[18px] md:grid-cols-2">
      {sources.map((source) => (
        <SourceCard key={source.key} source={source} enabled={enabledKeys.has(source.key)} />
      ))}
    </div>
  )
}

/** Onglets en barre (maquette) : Sources, puis chaque source activée. */
function SourceTabs({ sources, activeKey }) {
  const tabs = [{ key: null, label: 'Sources', to: '/dashboard/prospection' }, ...sources.map((s) => ({ key: s.key, label: s.name, to: `/dashboard/prospection/${s.key}` }))]
  return (
    <nav className="inline-flex max-w-full flex-wrap gap-0.5 rounded-[10px] border border-border bg-surface p-1" aria-label="Sources de prospection">
      {tabs.map((tab) => (
        <Link
          key={tab.label}
          to={tab.to}
          aria-current={tab.key === activeKey ? 'page' : undefined}
          className={`rounded-[7px] px-3 py-1.5 text-[13px] font-medium transition ${
            tab.key === activeKey ? 'bg-surface-active text-ink' : 'text-foreground-secondary hover:text-ink'
          }`}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}

/**
 * Prospection : une seule entrée pour toutes les recherches.
 *   /dashboard/prospection           -> choix de la source
 *   /dashboard/prospection/:source   -> formulaire et suite de cette source
 */
export default function ProspectionPage() {
  const { source: sourceKey } = useParams()
  const { user, isLoading } = useCurrentUser()

  if (isLoading) return null

  const sources = PROSPECTION_SOURCES.filter((s) => hasModule(user, s.module))
  const enabledKeys = new Set(sources.map((s) => s.key))
  if (!sourceKey) {
    return (
      <div className="flex flex-col gap-5">
        <SourceTabs sources={sources} activeKey={null} />
        <SourceChooser sources={PROSPECTION_SOURCES} enabledKeys={enabledKeys} />
      </div>
    )
  }

  const source = sources.find((s) => s.key === sourceKey)
  if (!source) return <Navigate to="/dashboard/prospection" replace />

  const Screen = SOURCE_SCREENS[source.key]

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <SourceTabs sources={sources} activeKey={source.key} />
        <Link
          to={`/dashboard/crm/${source.crmBase}`}
          className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:underline"
        >
          Voir les résultats dans les clients <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <Screen />
    </div>
  )
}
